// Generated by Copilot
import { sumBy } from 'lodash';

import Api from '@barboxx/api';
import { OrderStatus } from '@barboxx/types';

export interface CsvColumn<T> {
    header: string;
    value: (row: T) => string | number | null | undefined;
}

export interface OrdersExportQuery {
    sort?: string;
    status?: OrderStatus[];
    businessId?: number;
    startDateTime?: string;
    endDateTime?: string;
    pageSize?: number;
}

export interface OrderExportRow {
    id: number;
    business?: {
        place?: {
            name?: string;
        };
    };
    status?: string;
    subTotal?: number;
    serviceFee?: number;
    taxAmount?: number;
    tipAmount?: number;
    totalAmount?: number;
    expectedPayout?: number;
    applicationFee?: number;
    creditCardProcessingFee?: number;
    createdAt?: string;
    completedAt?: string;
    items?: Array<{
        quantity?: number;
    }>;
    customer?: {
        firstName?: string;
        lastName?: string;
    };
}

export interface SummaryExportRow {
    business?: {
        place?: {
            name?: string;
        };
    };
    orderCount?: number;
    customerCount?: number;
    orderItemCount?: number;
    drinksOrdered?: number;
    drinksRequested?: number;
    drinksFulfilled?: number;
    subTotal?: number;
    serviceFee?: number;
    taxAmount?: number;
    tipAmount?: number;
    totalAmount?: number;
    grossChargedAmount?: number;
    netChargedAmount?: number;
    refundedAmount?: number;
    paymentAdjustmentCount?: number;
    applicationFee?: number;
    expectedPayout?: number;
    creditCardProcessingFeesCollected?: number;
    stripeFees?: number;
    profit?: number;
}

const getOrderBusinessName = (row: OrderExportRow) =>
    row.business?.place?.name ?? '';

const getOrderItemCount = (row: OrderExportRow) =>
    sumBy(row.items, item => item.quantity ?? 0);

const getOrderCustomerName = (row: OrderExportRow) =>
    `${row.customer?.firstName ?? ''} ${row.customer?.lastName ?? ''}`.trim();

const getSummaryBusinessName = (row: SummaryExportRow) =>
    row.business?.place?.name ?? '';

const ORDER_CORE_COLUMNS: CsvColumn<OrderExportRow>[] = [
    { header: 'Order ID', value: row => row.id },
    {
        header: 'Business',
        value: getOrderBusinessName,
    },
    { header: 'Status', value: row => row.status },
    { header: 'SubTotal', value: row => row.subTotal },
];

const ORDER_BUSINESS_FINANCIAL_COLUMNS: CsvColumn<OrderExportRow>[] = [
    { header: 'Tax', value: row => row.taxAmount },
    { header: 'Tip', value: row => row.tipAmount },
    {
        header: 'Expected Payout',
        value: row => row.expectedPayout,
    },
];

const ORDER_INTERNAL_FINANCIAL_COLUMNS: CsvColumn<OrderExportRow>[] = [
    { header: 'Service Fee', value: row => row.serviceFee },
    { header: 'Tax', value: row => row.taxAmount },
    { header: 'Tip', value: row => row.tipAmount },
    { header: 'Total', value: row => row.totalAmount },
    {
        header: 'Expected Payout',
        value: row => row.expectedPayout,
    },
    {
        header: 'Credit Card Fees',
        value: row => row.creditCardProcessingFee,
    },
];

const ORDER_TRAILING_COLUMNS: CsvColumn<OrderExportRow>[] = [
    {
        header: 'Item Count',
        value: getOrderItemCount,
    },
    { header: 'Created At', value: row => row.createdAt },
    { header: 'Completed At', value: row => row.completedAt },
];

const ORDER_INTERNAL_ONLY_COLUMNS: CsvColumn<OrderExportRow>[] = [
    {
        header: 'Application Fee',
        value: row => row.applicationFee,
    },
    {
        header: 'Customer',
        value: getOrderCustomerName,
    },
];

const SUMMARY_BASE_COLUMNS: CsvColumn<SummaryExportRow>[] = [
    {
        header: 'Business',
        value: getSummaryBusinessName,
    },
    { header: 'Order Count', value: row => row.orderCount },
    { header: 'Customer Count', value: row => row.customerCount },
    {
        header: 'Order Item Count',
        value: row => row.orderItemCount,
    },
    {
        header: 'Drinks Requested',
        value: row => row.drinksRequested,
    },
    {
        header: 'Drinks Fulfilled',
        value: row => row.drinksFulfilled,
    },
    { header: 'SubTotal', value: row => row.subTotal },
    { header: 'Service Fees', value: row => row.serviceFee },
    { header: 'Taxes', value: row => row.taxAmount },
    { header: 'Tips', value: row => row.tipAmount },
    { header: 'Net Charged', value: row => row.netChargedAmount },
    {
        header: 'Gross Charged',
        value: row => row.grossChargedAmount,
    },
    {
        header: 'Refunded',
        value: row => row.refundedAmount,
    },
    {
        header: 'Payment Adjustments',
        value: row => row.paymentAdjustmentCount,
    },
    {
        header: 'Expected Payout',
        value: row => row.expectedPayout,
    },
    {
        header: 'Credit Card Fees Collected',
        value: row => row.creditCardProcessingFeesCollected,
    },
];

const SUMMARY_INTERNAL_ONLY_COLUMNS: CsvColumn<SummaryExportRow>[] = [
    {
        header: 'App Fees',
        value: row => row.applicationFee,
    },
    {
        header: 'Stripe Fees',
        value: row => row.stripeFees,
    },
    { header: 'Profit', value: row => row.profit },
];

const escapeCsvCell = (value: string) => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
    }

    return value;
};

const toCellValue = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value);
};

export const downloadCsv = <T>(
    fileName: string,
    rows: T[],
    columns: CsvColumn<T>[]
) => {
    const headerRow = columns.map(column => escapeCsvCell(column.header));

    const dataRows = rows.map(row =>
        columns
            .map(column => escapeCsvCell(toCellValue(column.value(row))))
            .join(',')
    );

    const csvContent = [headerRow.join(','), ...dataRows].join('\n');
    const blob = new Blob(['\uFEFF', csvContent], {
        type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const getOrdersExportColumns = (
    userType: string
): CsvColumn<OrderExportRow>[] => {
    if (userType === 'Business') {
        return [
            ...ORDER_CORE_COLUMNS,
            ...ORDER_BUSINESS_FINANCIAL_COLUMNS,
            ...ORDER_TRAILING_COLUMNS,
        ];
    }

    return [
        ...ORDER_CORE_COLUMNS,
        ...ORDER_INTERNAL_FINANCIAL_COLUMNS,
        ...ORDER_TRAILING_COLUMNS,
        ...ORDER_INTERNAL_ONLY_COLUMNS,
    ];
};

export const getSummaryExportColumns = (
    userType: string
): CsvColumn<SummaryExportRow>[] => {
    if (userType === 'Internal') {
        return [...SUMMARY_BASE_COLUMNS, ...SUMMARY_INTERNAL_ONLY_COLUMNS];
    }

    return SUMMARY_BASE_COLUMNS;
};

export const fetchAllOrdersForExport = async ({
    sort,
    status,
    businessId,
    startDateTime,
    endDateTime,
    pageSize = 500,
}: OrdersExportQuery): Promise<OrderExportRow[]> => {
    let exportPage = 0;
    let hasMore = true;
    const exportRows: OrderExportRow[] = [];

    while (hasMore) {
        const response = await Api.Orders.Order.getOrdersData({
            page: exportPage,
            size: pageSize,
            sort,
            status,
            businessId,
            startDateTime,
            endDateTime,
        });

        const pageRows = (response.data?.results ?? []) as OrderExportRow[];
        const total = response.data?.total ?? 0;

        exportRows.push(...pageRows);

        if (pageRows.length === 0 || exportRows.length >= total) {
            hasMore = false;
        } else {
            exportPage += 1;
        }
    }

    return exportRows;
};
